// The width of the right-hand dock (Inspector, Objects, Layers, Assets, Library).
// The person drags the dock's left edge, or uses the arrow keys on it. Pure, so the tests hold it.
//
//   least    = the default width. The dock is never narrower.
//   most     = 60 % of the window, and never so wide that the drawing is under 320 px.
//   a window so narrow that "most" is under "least": the dock stays at "least".
//
// The wanted width is stored, not the clamped one: the dock comes back to it when the window grows.
import { browserStorage, type StorageLike } from "../library/favourites";

export const DOCK_DEFAULT = 280;
export const DOCK_KEY = "tracecad.dock.width";
/** Narrowest drawing area that the dock may leave. */
export const DRAWING_MIN = 320;
/** The toolbar to the left of the drawing. */
export const TOOLBAR_WIDTH = 48;
export const DOCK_STEP = 16;
export const DOCK_STEP_BIG = 64;
export const DOCK_SHARE = 0.6;
/** A stored value above this is not a width. */
const STORED_MAX = 10000;

/** The widest the dock may be in a window of this width. Never below the default. */
export function dockMax(windowWidth: number): number {
  const w = Number.isFinite(windowWidth) ? windowWidth : 0;
  const byShare = Math.floor(w * DOCK_SHARE);
  const byDrawing = Math.floor(w - TOOLBAR_WIDTH - DRAWING_MIN);
  return Math.max(DOCK_DEFAULT, Math.min(byShare, byDrawing));
}

/** Keep a width between the default and the most the window allows. A value that is not a number gives the default. */
export function clampDockWidth(width: number, windowWidth: number): number {
  if (!Number.isFinite(width)) return DOCK_DEFAULT;
  return Math.min(dockMax(windowWidth), Math.max(DOCK_DEFAULT, Math.round(width)));
}

/** The width after an arrow key. direction -1 = wider (the left arrow), +1 = narrower (the right arrow). */
export function stepDockWidth(current: number, direction: -1 | 1, big: boolean, windowWidth: number): number {
  const step = big ? DOCK_STEP_BIG : DOCK_STEP;
  return clampDockWidth(current - direction * step, windowWidth);
}

/** The width after a drag: the dock grows when the pointer moves left of where it started. */
export function dragDockWidth(startWidth: number, startX: number, x: number, windowWidth: number): number {
  return clampDockWidth(startWidth + (startX - x), windowWidth);
}

/** A stored value as a width, or null when it is not a usable number. */
export function parseDockWidth(raw: string | null | undefined): number | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof v !== "number" || !Number.isFinite(v) || v < 1 || v > STORED_MAX) return null;
  return Math.round(v);
}

/** The width the person chose, or the default. Not clamped: the window decides that. */
export function loadDockWidth(storage: StorageLike | null = browserStorage()): number {
  if (!storage) return DOCK_DEFAULT;
  try {
    return parseDockWidth(storage.getItem(DOCK_KEY)) ?? DOCK_DEFAULT;
  } catch {
    return DOCK_DEFAULT;
  }
}

/** Keep the width. The default is stored as nothing, so a later change of the default reaches the person. */
export function saveDockWidth(width: number, storage: StorageLike | null = browserStorage()): boolean {
  if (!storage) return false;
  try {
    if (Math.round(width) === DOCK_DEFAULT && storage.removeItem) {
      storage.removeItem(DOCK_KEY);
    } else {
      storage.setItem(DOCK_KEY, String(Math.round(width)));
    }
    return true;
  } catch {
    return false;
  }
}
